import surveyAdapter from "../SurveyAdapter/Router";
import { CryptoTransitionCoordinator, EncryptionProvider, cryptoTelemetry } from "common/crypto";
import { isCryptoTestMigrationEnabled } from "common/crypto/testMigrationConfig";
import MasterKeyVerifierApi from "./masterKeyVerifierApi";

const autoMigrateEnabled = process.env.REACT_APP_BENCHMARKING_CRYPTO_AUTO_MIGRATE === "true";
const cryptoError = (message, cause, details = {}) => Object.assign(new Error(message), { cause, ...details });

export default class MasterKeyValidationAdapter {
  constructor(httpClient) {
    this.verifierApi = new MasterKeyVerifierApi(httpClient);
    this.transitionCoordinator = new CryptoTransitionCoordinator({
      loadEmployees: (companyHash) => surveyAdapter.getCryptoMigrationEmployees(companyHash),
      transitionService: { apply: (plan) => surveyAdapter.applyCryptoTransition(plan) },
      verifierApi: this.verifierApi,
      createTargetEngine: (masterKey, EngineType) => EncryptionProvider.createEngine(EngineType || EncryptionProvider.currentEngineType, masterKey),
      activateTarget: (engine, expectedRevision) => EncryptionProvider.activate(engine, expectedRevision),
      assertSession: (expectedRevision) => EncryptionProvider.assertRevision(expectedRevision),
    });
  }

  async validateEngine(engine) {
    return this.verifierApi.validateHash(await engine.getValidationHash());
  }

  normalizeValid(response, engine, extra = {}) {
    return {
      ...response,
      status: response?.status || 200,
      data: { ...(response?.data || {}), ...extra, valid: true, cryptoVersion: engine.version },
    };
  }

  normalizeInvalid(response = {}) {
    return { ...response, status: response?.status || 200, data: { ...(response?.data || {}), valid: false } };
  }

  activeSessionDetails() {
    return { cryptoSessionRevision: EncryptionProvider.getSnapshot().revision };
  }

  clearSurveyCache() {
    try {
      surveyAdapter.clearCollectionDataCache();
    } catch (error) {
      console.error("Failed to clear the survey cache after a master-key operation", error);
    }
  }

  clearRecoverySession(error) {
    if (!error?.requiresRecovery) return;
    this.clearSurveyCache();
    if (EncryptionProvider.isInitialized() || EncryptionProvider.hasStoredSession()) EncryptionProvider.purge();
  }

  record(operation, details = {}) {
    return cryptoTelemetry.record({ operation, ...details });
  }

  async identify(masterKey, expectedRevision) {
    return EncryptionProvider.identify(masterKey, (engine) => this.validateEngine(engine), expectedRevision);
  }

  async verify(masterKey) {
    if (!masterKey) throw new Error("Cannot verify an empty master key");
    const expected = EncryptionProvider.captureRevision();
    return EncryptionProvider.runExclusive(async () => {
      EncryptionProvider.assertRevision(expected);
      const result = await this.identify(masterKey, expected);
      if (!result.valid) {
        this.record("verify", { succeeded: false, failureStage: "validation" });
        return this.normalizeInvalid(result.response);
      }

      try {
        const response = this.normalizeValid(result.response, result.engine);
        this.record("verify", { succeeded: true, sourceVersion: result.engine.version });
        return response;
      } finally {
        result.engine.purge();
      }
    });
  }

  async isValid(masterKey) {
    if (!masterKey) throw new Error("Cannot validate an empty master key");
    const expected = EncryptionProvider.captureRevision();

    return EncryptionProvider.runExclusive(async () => {
      EncryptionProvider.assertRevision(expected);
      const source = await this.identify(masterKey, expected);

      if (!source.valid) {
        this.record("enter", { succeeded: false, failureStage: "validation" });
        return this.normalizeInvalid(source.response);
      }

      const isCurrent = source.engine.constructor === EncryptionProvider.currentEngineType;

      if (isCurrent || !autoMigrateEnabled) {
        try {
          EncryptionProvider.activate(source.engine, expected);
          this.clearSurveyCache();

          this.record("enter", {
            succeeded: true,
            sourceVersion: source.engine.version,
            targetVersion: source.engine.version,
            migrationAttempted: false,
          });

          return this.normalizeValid(source.response, source.engine, {
            migrationAttempted: false,
            ...this.activeSessionDetails(),
          });
        } catch (error) {
          source.engine.purge();

          this.record("enter", {
            succeeded: false,
            sourceVersion: source.engine.version,
            targetVersion: source.engine.version,
            migrationAttempted: false,
            failureStage: "local-activation",
            safeToContinue: true,
          });

          throw cryptoError("The master key was validated, but the local crypto session could not be activated", error, {
            failureStage: "local-activation",
            safeToContinue: true,
          });
        }
      }

      const targetVersion = EncryptionProvider.currentEngineType.VERSION;

      try {
        const committed = await this.transitionCoordinator.commit({
          sourceEngine: source.engine,
          targetMasterKey: masterKey,
          expectedRevision: expected,
        });

        this.clearSurveyCache();

        this.record("migration", {
          succeeded: true,
          sourceVersion: committed.sourceVersion,
          targetVersion: committed.targetVersion,
          migrationAttempted: true,
          migrationSucceeded: true,
        });

        return {
          ...committed.verifierResponse,
          status: committed.verifierResponse?.status || 200,
          data: {
            ...(committed.verifierResponse?.data || {}),
            valid: true,
            cryptoVersion: committed.targetVersion,
            migrationAttempted: true,
            migrationSucceeded: true,
            ...this.activeSessionDetails(),
          },
        };
      } catch (error) {
        if (!error?.safeToContinue) {
          source.engine.purge();
          this.clearRecoverySession(error);

          this.record("migration", {
            succeeded: false,
            sourceVersion: source.engine.version,
            targetVersion,
            migrationAttempted: true,
            migrationSucceeded: false,
            failureStage: error?.failureStage || "unknown",
            safeToContinue: false,
          });

          throw error;
        }

        try {
          EncryptionProvider.activate(source.engine, expected);
          this.clearSurveyCache();
        } catch (activationError) {
          source.engine.purge();

          this.record("migration", {
            succeeded: false,
            sourceVersion: source.engine.version,
            targetVersion,
            migrationAttempted: true,
            migrationSucceeded: false,
            failureStage: "local-activation",
            safeToContinue: true,
          });

          throw cryptoError("Legacy authentication succeeded, but the local crypto session could not be activated", activationError, {
            failureStage: "local-activation",
            safeToContinue: true,
          });
        }

        this.record("migration", {
          succeeded: false,
          sourceVersion: source.engine.version,
          targetVersion,
          migrationAttempted: true,
          migrationSucceeded: false,
          failureStage: error?.failureStage || "pre-persistence",
          safeToContinue: true,
        });

        return this.normalizeValid(source.response, source.engine, {
          migrationAttempted: true,
          migrationSucceeded: false,
          ...this.activeSessionDetails(),
        });
      }
    });
  }

  async provision(operation, masterKey, { activateSession = true } = {}) {
    if (!masterKey) throw new Error(`Cannot ${operation} using an empty master key`);
    const expected = EncryptionProvider.captureRevision();

    return EncryptionProvider.runExclusive(async () => {
      EncryptionProvider.assertRevision(expected);

      let engine;
      try {
        engine = await EncryptionProvider.createCurrentEngine(masterKey);
      } catch (error) {
        throw cryptoError("Target crypto engine initialization failed before persistence", error, {
          failureStage: "target-engine",
          safeToContinue: true,
          requiresRecovery: false,
          persistenceState: "source",
        });
      }

      let remotePersisted = false;

      try {
        let hash;

        try {
          hash = await engine.getValidationHash();
        } catch (error) {
          throw cryptoError("Master-key validation hash derivation failed before persistence", error, {
            failureStage: "validation-hash",
            safeToContinue: true,
            requiresRecovery: false,
            persistenceState: "source",
          });
        }

        const response = operation === "add" ? await this.verifierApi.addHash(hash) : await this.verifierApi.resetHash(hash);

        remotePersisted = true;

        if (activateSession) {
          EncryptionProvider.activate(engine, expected);
        } else {
          engine.purge();
          if (EncryptionProvider.isInitialized() || EncryptionProvider.hasStoredSession()) EncryptionProvider.purge();
        }

        const normalized = this.normalizeValid(response, engine, activateSession ? this.activeSessionDetails() : {});

        this.clearSurveyCache();

        this.record(operation === "add" ? "create" : "reset", {
          succeeded: true,
          targetVersion: normalized.data.cryptoVersion,
          sessionActivated: activateSession,
        });

        return normalized;
      } catch (error) {
        engine.purge();

        const classifiedError = remotePersisted
          ? cryptoError("The master-key operation succeeded remotely, but the local crypto session could not be activated", error, {
              failureStage: "local-activation",
              requiresRecovery: true,
              safeToContinue: false,
              persistenceState: "target",
            })
          : error;

        this.clearRecoverySession(classifiedError);

        this.record(operation === "add" ? "create" : "reset", {
          succeeded: false,
          targetVersion: engine.version,
          failureStage: classifiedError?.failureStage || "verifier-update",
          safeToContinue: Boolean(classifiedError?.safeToContinue),
        });

        throw classifiedError;
      }
    });
  }

  add(masterKey) {
    return this.provision("add", masterKey);
  }

  reset(masterKey, options = {}) {
    return this.provision("reset", masterKey, options);
  }

  exists() {
    return this.verifierApi.exists();
  }

  async prepareChange(oldMasterKey, newMasterKey) {
    if (!oldMasterKey || !newMasterKey) throw new Error("Both old and new master keys are required");
    if (oldMasterKey === newMasterKey) throw new Error("The old and new master keys must differ");

    const expected = EncryptionProvider.captureRevision();

    return EncryptionProvider.runExclusive(async () => {
      EncryptionProvider.assertRevision(expected);

      const source = await this.identify(oldMasterKey, expected);
      if (!source.valid) return { valid: false, response: this.normalizeInvalid(source.response) };

      let consumed = false;

      const release = () => {
        try {
          source.engine.purge();
        } catch (error) {
          console.error("Prepared master-key change could not purge its detached source engine", error);
        }
      };

      return {
        valid: true,
        response: this.normalizeValid(source.response, source.engine),

        cancel() {
          if (consumed) return;
          consumed = true;
          release();
        },

        confirm: () => {
          if (consumed) return Promise.reject(new Error("The prepared master-key change is no longer available"));
          consumed = true;

          return EncryptionProvider.runExclusive(async () => {
            try {
              EncryptionProvider.assertRevision(expected);

              const committed = await this.transitionCoordinator.commit({
                sourceEngine: source.engine,
                targetMasterKey: newMasterKey,
                expectedRevision: expected,
              });

              this.clearSurveyCache();

              this.record("change", {
                succeeded: true,
                sourceVersion: committed.sourceVersion,
                targetVersion: committed.targetVersion,
              });

              return {
                ...committed.verifierResponse,
                status: committed.verifierResponse?.status || 200,
                data: {
                  ...(committed.verifierResponse?.data || {}),
                  valid: true,
                  cryptoVersion: committed.targetVersion,
                  ...this.activeSessionDetails(),
                },
              };
            } catch (error) {
              release();
              this.clearRecoverySession(error);

              this.record("change", {
                succeeded: false,
                sourceVersion: source.engine.version,
                targetVersion: EncryptionProvider.currentEngineType.VERSION,
                failureStage: error?.failureStage || "unknown",
                safeToContinue: Boolean(error?.safeToContinue),
              });

              throw error;
            }
          });
        },
      };
    });
  }

  async migrateForTesting(oldMasterKey, newMasterKey, targetVersion) {
    if (!isCryptoTestMigrationEnabled()) throw new Error("QA crypto migration is disabled");
    if (!oldMasterKey) throw new Error("The current master key is required");

    const targetMasterKey = newMasterKey || oldMasterKey;
    const TargetEngineType = EncryptionProvider.getEngineType(targetVersion);

    if (!TargetEngineType) throw new Error(`Unsupported target crypto version: ${targetVersion}`);

    const expected = EncryptionProvider.captureRevision();

    return EncryptionProvider.runExclusive(async () => {
      EncryptionProvider.assertRevision(expected);

      const activeSession = EncryptionProvider.requireSnapshot();
      const source = await this.identify(oldMasterKey, expected);

      if (!source.valid) {
        this.record("qa-migration", {
          succeeded: false,
          targetVersion,
          failureStage: "validation",
          safeToContinue: true,
        });

        throw new Error("The current master key is incorrect");
      }

      let sourceCompanyHash;

      try {
        sourceCompanyHash = await source.engine.getCompanyHash();

        if (source.engine.version !== activeSession.engineVersion || sourceCompanyHash !== activeSession.companyHash) {
          throw new Error("The entered master key does not match the active crypto session");
        }

        if (source.engine.version === targetVersion && oldMasterKey === targetMasterKey) {
          throw new Error(`The account is already using ${targetVersion.toUpperCase()} with this master key`);
        }

        const committed = await this.transitionCoordinator.commit({
          sourceEngine: source.engine,
          targetMasterKey,
          targetEngineType: TargetEngineType,
          expectedRevision: expected,
        });

        this.clearSurveyCache();

        this.record("qa-migration", {
          succeeded: true,
          sourceVersion: committed.sourceVersion,
          targetVersion: committed.targetVersion,
        });

        return {
          ...committed.verifierResponse,
          status: committed.verifierResponse?.status || 200,
          data: {
            ...(committed.verifierResponse?.data || {}),
            valid: true,
            cryptoVersion: committed.targetVersion,
            qaMigration: true,
            ...this.activeSessionDetails(),
          },
        };
      } catch (error) {
        try {
          source.engine.purge();
        } catch (cleanupError) {
          console.error("QA crypto migration could not purge its detached source engine", cleanupError);
        }

        this.clearRecoverySession(error);

        this.record("qa-migration", {
          succeeded: false,
          sourceVersion: source.engine.version,
          targetVersion,
          failureStage: error?.failureStage || "qa-migration",
          safeToContinue: Boolean(error?.safeToContinue),
        });

        throw error;
      }
    });
  }

  async change(oldMasterKey, newMasterKey) {
    const prepared = await this.prepareChange(oldMasterKey, newMasterKey);
    if (!prepared.valid) throw new Error("The current master key is incorrect");
    return prepared.confirm();
  }
}
